/**
 * The operator's account-approval desk. Automatic checks collect the evidence;
 * a person resolves every blocking finding and gives the final approval before
 * a client can use the real dashboard.
 */

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { useToast } from "../lib/toast.js";
import { canApproveAfterAssumptions } from "../lib/assumptions-approval-readiness.js";
import { useAccountChange } from "../lib/account-context.js";
import { Card } from "../components/cards.js";
import { VerificationRunStatus, verificationIsRunning, verificationBlocksApproval, type VerificationJob } from "../components/VerificationRunStatus.js";
import {
  AgencyAssumptionsReview,
  type AssumptionsReviewReadiness,
} from "../components/AgencyAssumptionsReview.js";

type Severity = "red" | "amber" | "info";
type FixKind = "system" | "needs_costs" | "confirm_offer";
type OnboardingVerificationStage = "not_started" | "checking" | "needs_answers" | "team_review" | "verified";
type NotificationStatus = "sent" | "failed" | "not_configured" | "no_recipient";
type TeamReviewNotificationStatus = "pending" | "sent" | "failed" | "not_configured";

interface ReadyNotification {
  status: NotificationStatus;
  attemptedAt: string;
  sentAt?: string;
  recipientCount: number;
}

interface TeamReviewNotification {
  requestedAt: string;
  status: TeamReviewNotificationStatus;
  attemptedAt: string;
  sentAt?: string;
  recipientCount: number;
}

interface DataHealthFinding {
  severity: Severity;
  rule: string;
  plainTitle: string;
  plainDetail: string;
  fixKind: FixKind;
  offerFingerprint?: string;
}

interface AccountDataHealth {
  accountId: string;
  accountName: string;
  ranAt: string | null;
  neverRun: boolean;
  ok: boolean;
  red: number;
  amber: number;
  info: number;
  checksPassed: number;
  ordersTested: number;
  structuresTested: number;
  awaitingCosts: boolean;
  onboardingVerificationStage: OnboardingVerificationStage;
  onboardingReviewRequestedAt: string | null;
  onboardingApprovedAt: string | null;
  onboardingNotification: ReadyNotification | null;
  teamReviewNotification: TeamReviewNotification | null;
  findings: DataHealthFinding[];
  verificationJob?: VerificationJob | null;
}

interface FleetDataHealth {
  accounts: AccountDataHealth[];
  redAccounts: number;
  neverRunAccounts: number;
  teamReviewAccounts: number;
  attentionAccounts: number;
}

interface ApprovalResponse {
  ok: true;
  notification: ReadyNotification;
}

export default function AgencyDataHealthPage(): React.ReactElement {
  const api = useApiClient();
  const changeAccount = useAccountChange();
  const qc = useQueryClient();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [expanded, setExpanded] = useState<string | null>(searchParams.get("account"));
  const [runningId, setRunningId] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [notifyingId, setNotifyingId] = useState<string | null>(null);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [confirmingRule, setConfirmingRule] = useState<string | null>(null);

  const status = useQuery({
    queryKey: ["account", "agency-status"],
    queryFn: () => api.get<{ isOperator: boolean }>("/account/agency-status"),
  });
  const isOperator = status.data?.isOperator ?? false;

  const q = useQuery({
    queryKey: ["admin", "data-health"],
    enabled: isOperator,
    queryFn: () => api.get<FleetDataHealth>("/admin/data-health"),
    refetchInterval: (query) => query.state.data?.accounts.some((account) => verificationIsRunning(account.verificationJob)) ? 5_000 : 30_000,
  });

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ["admin", "data-health"] });
    void qc.invalidateQueries({ queryKey: ["admin", "issues"] });
  };

  const rememberJob = (accountId: string, job: VerificationJob): void => {
    qc.setQueryData<FleetDataHealth>(["admin", "data-health"], (current) => current ? {
      ...current,
      accounts: current.accounts.map((account) => account.accountId === accountId ? { ...account, verificationJob: job } : account),
    } : current);
  };

  const runNow = useMutation({
    mutationFn: (accountId: string) => api.post<{ job: VerificationJob; alreadyRunning: boolean }>(`/admin/clients/${accountId}/verify/run`),
    onMutate: (accountId: string) => setRunningId(accountId),
    onSuccess: (result, accountId) => {
      rememberJob(accountId, result.job);
      toast.success(result.alreadyRunning ? "This check is already running. Its progress is shown here." : "Checks are starting. You can leave this page and come back.");
      refresh();
    },
    onError: () => { toast.error("We could not confirm that the check started. Refresh this page to see its saved status before trying again."); refresh(); },
    onSettled: () => setRunningId(null),
  });

  const approveOnboarding = useMutation({
    mutationFn: (input: { accountId: string; warningIds: string[]; reviewNote: string }) => api.post<ApprovalResponse>(
      `/admin/clients/${input.accountId}/verify/approve-onboarding`,
      { assumptionWarningIds: input.warningIds, assumptionReviewNote: input.reviewNote },
    ),
    onMutate: (input) => setApprovingId(input.accountId),
    onSuccess: (result) => {
      if (result.notification.status === "sent") {
        toast.success("Approved. The dashboard is live and the ready email was sent.");
      } else {
        toast.success("Approved. The dashboard is live, but the ready email still needs attention.");
      }
      refresh();
    },
    onError: (error) => toast.error(apiError(error, "This account could not be approved.")),
    onSettled: () => setApprovingId(null),
  });

  const retryReadyEmail = useMutation({
    mutationFn: (accountId: string) => api.post<{ notification: ReadyNotification }>(`/admin/clients/${accountId}/verify/send-ready-email`),
    onMutate: (accountId: string) => setNotifyingId(accountId),
    onSuccess: (result) => {
      if (result.notification.status === "sent") toast.success("Ready email sent.");
      else toast.error(notificationMessage(result.notification));
      refresh();
    },
    onError: (error) => toast.error(apiError(error, "The ready email could not be sent.")),
    onSettled: () => setNotifyingId(null),
  });

  const confirmOffer = useMutation({
    mutationFn: (input: { accountId: string; fingerprint: string; rule: string }) => api.post<{ job: VerificationJob }>(
      `/admin/clients/${input.accountId}/verify/confirm-offer`,
      { fingerprint: input.fingerprint },
    ),
    onMutate: (input) => setConfirmingRule(`${input.accountId}:${input.rule}`),
    onSuccess: (result, input) => {
      rememberJob(input.accountId, result.job);
      toast.success("Offer confirmed. The checks are starting again.");
      refresh();
    },
    onError: (error) => toast.error(apiError(error, "That offer could not be confirmed.")),
    onSettled: () => setConfirmingRule(null),
  });

  const switchTo = async (accountId: string, destination: string): Promise<void> => {
    setSwitchingId(accountId);
    try {
      await changeAccount({
        path: "/account/switch",
        body: { accountId },
        targetAccountId: accountId,
        destination,
      });
    } catch {
      setSwitchingId(null);
      toast.error("Could not open that client account.");
    }
  };

  if (status.isLoading) return <p style={{ color: "var(--body)" }}>Loading…</p>;
  if (!isOperator) {
    return (
      <div>
        <h1 style={{ margin: 0, fontSize: 24 }}>Account review</h1>
        <p style={{ color: "var(--body)", marginTop: 10 }}>This area is only available to agency operators.</p>
      </div>
    );
  }

  const accounts = q.data?.accounts ?? [];
  const readyCount = accounts.filter((account) => account.ok && !account.awaitingCosts && account.structuresTested > 0 && account.onboardingVerificationStage === "team_review"
    && !verificationBlocksApproval(account.verificationJob, account.ranAt)).length;
  const liveCount = accounts.filter((account) => account.onboardingVerificationStage === "verified").length;

  return (
    <div>
      <div style={kicker}>Agency</div>
      <h1 style={{ margin: "7px 0 8px", fontSize: 27 }}>Which accounts are ready to go live?</h1>
      <p style={{ color: "var(--body)", fontSize: 14.5, maxWidth: 820, lineHeight: 1.5, marginTop: 0 }}>
        Automatic checks run every night. A client only gets their real dashboard after every important problem is resolved and your team approves the account here.
      </p>

      {q.data && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
          <SummaryPill label="Need work" value={accounts.filter((account) => account.red > 0 || account.amber > 0 || (!account.neverRun && (account.awaitingCosts || account.structuresTested < 1))).length} tone={q.data.redAccounts > 0 ? "red" : "amber"} />
          <SummaryPill label="Waiting for team review" value={q.data.teamReviewAccounts} tone={q.data.teamReviewAccounts > 0 ? "amber" : "neutral"} />
          <SummaryPill label="Automatic checks clean" value={readyCount} tone={readyCount > 0 ? "amber" : "neutral"} />
          <SummaryPill label="Live for clients" value={liveCount} tone="good" />
          <SummaryPill label="Never checked" value={q.data.neverRunAccounts} tone="neutral" />
        </div>
      )}

      {q.isLoading && <p style={{ marginTop: 18, color: "var(--body)" }}>Checking every client…</p>}
      {q.isError && <p style={{ marginTop: 18, color: "var(--bad)" }}>Account review could not load. Refresh to retry.</p>}
      {q.data && accounts.length === 0 && (
        <Card style={{ marginTop: 16 }}>
          <p style={{ color: "var(--body)", fontSize: 13.5, margin: 0 }}>No client accounts yet.</p>
        </Card>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
        {accounts.map((account) => {
          const isOpen = expanded === account.accountId;
          const statusInfo = accountStatus(account);
          return (
            <Card key={account.accountId} style={{ padding: 0, overflow: "hidden" }}>
              <button type="button" onClick={() => setExpanded(isOpen ? null : account.accountId)} style={rowButton} aria-expanded={isOpen}>
                <span style={{ ...statusDot, background: toneColor(statusInfo.tone) }} />
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <div style={{ fontSize: 14, color: "var(--text)", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {account.accountName}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--body)", marginTop: 3 }}>{summaryLine(account)}</div>
                </div>
                <span style={statusChip(statusInfo.tone)}>{statusInfo.label}</span>
                <span aria-hidden="true" style={{ color: "var(--body-faint)", fontSize: 12 }}>{isOpen ? "▲" : "▼"}</span>
              </button>

              {isOpen && (
                <AccountReview
                  account={account}
                  running={runningId === account.accountId || verificationIsRunning(account.verificationJob)}
                  approving={approvingId === account.accountId}
                  notifying={notifyingId === account.accountId}
                  switching={switchingId === account.accountId}
                  confirmingRule={confirmingRule}
                  onRun={() => runNow.mutate(account.accountId)}
                  onApprove={(acceptance) => approveOnboarding.mutate({ accountId: account.accountId, ...acceptance })}
                  onRetryEmail={() => retryReadyEmail.mutate(account.accountId)}
                  onOpen={(destination) => void switchTo(account.accountId, destination)}
                  onConfirmOffer={(finding) => {
                    if (!finding.offerFingerprint) return;
                    const confirmed = window.confirm("Confirm that this offer is intentional? This removes the warning and runs every check again.");
                    if (confirmed) confirmOffer.mutate({ accountId: account.accountId, fingerprint: finding.offerFingerprint, rule: finding.rule });
                  }}
                />
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function AccountReview({
  account,
  running,
  approving,
  notifying,
  switching,
  confirmingRule,
  onRun,
  onApprove,
  onRetryEmail,
  onOpen,
  onConfirmOffer,
}: {
  account: AccountDataHealth;
  running: boolean;
  approving: boolean;
  notifying: boolean;
  switching: boolean;
  confirmingRule: string | null;
  onRun: () => void;
  onApprove: (acceptance: { warningIds: string[]; reviewNote: string }) => void;
  onRetryEmail: () => void;
  onOpen: (destination: string) => void;
  onConfirmOffer: (finding: DataHealthFinding) => void;
}): React.ReactElement {
  const [assumptions, setAssumptions] = useState<AssumptionsReviewReadiness>({
    status: "not_loaded",
    hardBlockers: 0,
    warningIds: [],
    acceptedWarningIds: [],
    reviewNote: "",
  });
  const blocking = account.findings.filter((finding) => finding.severity !== "info");
  const notes = account.findings.filter((finding) => finding.severity === "info");
  const setupGaps = [
    ...(account.structuresTested < 1 && !account.neverRun ? ["orders" as const] : []),
    ...(account.awaitingCosts && !account.neverRun ? ["costs" as const] : []),
  ];
  const isLive = account.onboardingVerificationStage === "verified";
  const automaticChecksReady = account.ok && setupGaps.length === 0 && account.onboardingVerificationStage === "team_review";
  const isReady = !verificationBlocksApproval(account.verificationJob, account.ranAt)
    && canApproveAfterAssumptions(automaticChecksReady, assumptions);
  const waitingForSetup = !isLive && account.onboardingVerificationStage !== "team_review";
  return (
    <div style={{ padding: "0 16px 16px" }}>
      <VerificationRunStatus job={account.verificationJob} reportRanAt={account.ranAt} onRetry={onRun} />
      <ReviewTimeline account={account} assumptions={assumptions} />
      <AgencyAssumptionsReview accountId={account.accountId} onReadinessChange={setAssumptions} />
      <div style={reviewGrid}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
          {blocking.length > 0 || setupGaps.length > 0 ? (
            <section style={sectionCard}>
              <div style={sectionHeadingRow}>
                <h3 style={sectionHeading}>What needs attention</h3>
                <span style={statusChip(account.red > 0 ? "red" : "amber")}>{blocking.length + setupGaps.length} open</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 10 }}>
                {setupGaps.map((gap) => (
                  <SetupGapRow key={gap} gap={gap} busy={switching} onOpen={onOpen} />
                ))}
                {blocking.map((finding) => (
                  <FindingRow
                    key={`${finding.rule}:${finding.plainTitle}`}
                    finding={finding}
                    busy={switching || confirmingRule === `${account.accountId}:${finding.rule}` || (running && finding.fixKind === "confirm_offer")}
                    onOpen={onOpen}
                    onConfirm={() => onConfirmOffer(finding)}
                  />
                ))}
              </div>
              <p style={{ color: "var(--body-faint)", fontSize: 11.5, margin: "10px 0 0" }}>The client cannot approve these items themselves.</p>
            </section>
          ) : !account.neverRun && (assumptions.status !== "loaded" || assumptions.hardBlockers > 0) ? (
            <section style={sectionCard}>
              <div style={sectionHeadingRow}>
                <h3 style={sectionHeading}>{assumptions.status === "not_loaded" ? "Review the saved assumptions" : assumptions.status === "loading" ? "Checking every saved assumption" : assumptions.status === "error" ? "The assumptions review did not load" : "Saved assumptions need attention"}</h3>
                <span style={statusChip("amber")}>{assumptions.status === "loaded" ? `${assumptions.hardBlockers} open` : "Not ready"}</span>
              </div>
              <p style={{ color: "var(--body)", fontSize: 13, lineHeight: 1.55, margin: "8px 0 0" }}>
                {assumptions.status === "not_loaded"
                  ? "The automatic checks are clean. Open the assumptions review above to check every product, cost, fee, and cash-timing input before approval."
                  : assumptions.status === "loading"
                  ? "The automatic checks are clean. We are still loading the product, cost, and source assumptions before approval."
                  : assumptions.status === "error"
                    ? "The automatic checks are clean, but the deeper assumptions review could not load. Refresh before approving this account."
                    : `The automatic checks are clean, but ${assumptions.hardBlockers} saved assumption${assumptions.hardBlockers === 1 ? "" : "s"} must be fixed before approval. Open the assumptions review above to see each one.`}
              </p>
            </section>
          ) : (
            <section style={sectionCard}>
              <div style={sectionHeadingRow}>
                <h3 style={sectionHeading}>{account.neverRun ? "Run the first check" : "Ready for your approval"}</h3>
                {!account.neverRun && <span style={statusChip("good")}>Clean</span>}
              </div>
              <p style={{ color: "var(--body)", fontSize: 13, lineHeight: 1.55, margin: "8px 0 0" }}>
                {account.neverRun
                  ? "This account has not been checked yet. Run the checks before reviewing or approving it."
                  : isLive
                    ? "This account passed its checks and team review. The real dashboard is available to the client."
                    : waitingForSetup
                      ? "The checks are clean, but the client has not finished setup yet. Approval becomes available after they finish."
                      : "The latest check found no remaining problems. Your approval is the final step before the client gets their real dashboard."}
              </p>
            </section>
          )}

          {!account.neverRun && (
            <section style={sectionCard}>
              <h3 style={sectionHeading}>What passed</h3>
              <ul style={passedList}>
                <li><b>{account.checksPassed.toLocaleString()}</b> automatic checks passed</li>
                <li><b>{account.ordersTested.toLocaleString()}</b> real orders tested</li>
                <li><b>{account.structuresTested.toLocaleString()}</b> different order types tested</li>
                <li>{account.awaitingCosts ? "Product margin checks are waiting for product costs" : "Product cost and margin checks were included"}</li>
              </ul>
            </section>
          )}

          {notes.length > 0 && (
            <section style={sectionCard}>
              <h3 style={sectionHeading}>Additional notes</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                {notes.map((finding) => (
                  <div key={`${finding.rule}:${finding.plainTitle}`} style={noteRow}>
                    <b style={{ color: "var(--text)", fontSize: 12.5 }}>{finding.plainTitle}</b>
                    <p style={{ color: "var(--body)", fontSize: 12, lineHeight: 1.5, margin: "4px 0 0" }}>{finding.plainDetail}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <ApprovalPanel
          account={account}
          ready={isReady}
          assumptions={assumptions}
          live={isLive}
          running={running}
          approving={approving}
          notifying={notifying}
          onRun={onRun}
          onApprove={() => onApprove({ warningIds: assumptions.acceptedWarningIds, reviewNote: assumptions.reviewNote })}
          onRetryEmail={onRetryEmail}
        />
      </div>
    </div>
  );
}

function ReviewTimeline({ account, assumptions }: { account: AccountDataHealth; assumptions: AssumptionsReviewReadiness }): React.ReactElement {
  const checked = !account.neverRun;
  const live = account.onboardingVerificationStage === "verified";
  const fullEvidence = checked && !account.awaitingCosts && account.structuresTested > 0;
  const reviewDetail = account.neverRun
    ? "Waiting for the first check"
    : live
      ? "Approved after a clean automatic check"
      : account.onboardingVerificationStage !== "team_review"
        ? "Waiting for the client to finish setup"
        : account.awaitingCosts || account.structuresTested < 1
          ? "Finish the missing setup, then run the checks again"
        : account.red > 0 || account.amber > 0
          ? "Resolve every open item, then run the checks again"
          : assumptions.status === "not_loaded"
            ? "Automatic checks are clean. Open the saved assumptions review"
            : assumptions.status === "loading"
            ? "Automatic checks are clean. Loading the saved assumptions"
            : assumptions.status === "error"
              ? "The saved assumptions review did not load"
              : assumptions.hardBlockers > 0
                ? `${assumptions.hardBlockers} saved assumption${assumptions.hardBlockers === 1 ? "" : "s"} still need attention`
                : "Automatic checks and saved assumptions are clean. Your approval is the final step";
  return (
    <div style={timeline}>
      <TimelineStep
        label="Automatic checks"
        detail={!checked
          ? "Not run yet"
          : fullEvidence
            ? "Orders, costs, calculations, and duplicate protection checked"
            : `${account.checksPassed.toLocaleString()} preliminary checks ran. Real order and profit checks still need setup`}
        state={fullEvidence ? "done" : checked ? "active" : "waiting"}
      />
      <TimelineStep label="Team review" detail={reviewDetail} state={live ? "done" : checked ? "active" : "waiting"} />
      <TimelineStep label="Client access" detail={live ? "The client can open their real dashboard now" : "The real dashboard stays locked until you approve it"} state={live ? "done" : "waiting"} />
    </div>
  );
}

function TimelineStep({ label, detail, state }: { label: string; detail: string; state: "done" | "active" | "waiting" }): React.ReactElement {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "26px 1fr", gap: 9, minWidth: 0 }}>
      <span style={{ ...timelineIcon, ...(state === "done" ? timelineDone : state === "active" ? timelineActive : {}) }}>{state === "done" ? "✓" : state === "active" ? "•" : "○"}</span>
      <div>
        <div style={{ color: "var(--text)", fontWeight: 700, fontSize: 12.5 }}>{label}</div>
        <div style={{ color: "var(--body)", fontSize: 11.5, lineHeight: 1.4, marginTop: 2 }}>{detail}</div>
      </div>
    </div>
  );
}

function FindingRow({ finding, busy, onOpen, onConfirm }: { finding: DataHealthFinding; busy: boolean; onOpen: (destination: string) => void; onConfirm: () => void }): React.ReactElement {
  const red = finding.severity === "red";
  const label = red
    ? "We fix this"
    : finding.fixKind === "confirm_offer"
      ? "Needs a decision"
      : finding.fixKind === "system"
        ? "Team review"
        : "Needs client data";
  const action = findingAction(finding);
  return (
    <div style={{ ...findingRow, borderLeftColor: red ? "var(--bad)" : "var(--warn, #E5A83B)" }}>
      <div style={sectionHeadingRow}>
        <b style={{ color: "var(--text)", fontSize: 13 }}>{finding.plainTitle}</b>
        <span style={statusChip(red ? "red" : "amber")}>{label}</span>
      </div>
      <p style={{ color: "var(--body)", fontSize: 12.5, lineHeight: 1.55, margin: "6px 0 0" }}>{finding.plainDetail}</p>
      {finding.fixKind === "confirm_offer" && finding.offerFingerprint ? (
        <button type="button" style={smallButton} disabled={busy} onClick={onConfirm}>{busy ? "Checking again…" : "Confirm this is intentional"}</button>
      ) : (
        <button type="button" style={smallButton} disabled={busy} onClick={() => {
          if (red) window.location.assign("/agency/issues");
          else onOpen(action.destination);
        }}>{busy ? "Opening…" : red ? "Open in Issues" : action.label}</button>
      )}
    </div>
  );
}

function SetupGapRow({ gap, busy, onOpen }: { gap: "orders" | "costs"; busy: boolean; onOpen: (destination: string) => void }): React.ReactElement {
  const orders = gap === "orders";
  return (
    <div style={{ ...findingRow, borderLeftColor: "var(--warn, #E5A83B)" }}>
      <div style={sectionHeadingRow}>
        <b style={{ color: "var(--text)", fontSize: 13 }}>{orders ? "No real orders are available to test" : "Product costs are not finished"}</b>
        <span style={statusChip("amber")}>Setup needed</span>
      </div>
      <p style={{ color: "var(--body)", fontSize: 12.5, lineHeight: 1.55, margin: "6px 0 0" }}>
        {orders
          ? "Connect and sync the store before approval. A clean formula test is not proof that the client's real orders are right."
          : "Finish the product costs before approval so the contribution margin and profit checks can run on real orders."}
      </p>
      <button type="button" style={smallButton} disabled={busy} onClick={() => onOpen(orders ? "/connections" : "/onboarding/chat?stage=costs")}>
        {busy ? "Opening…" : orders ? "Open connections" : "Open product costs"}
      </button>
    </div>
  );
}

function ApprovalPanel({ account, ready, assumptions, live, running, approving, notifying, onRun, onApprove, onRetryEmail }: {
  account: AccountDataHealth;
  ready: boolean;
  assumptions: AssumptionsReviewReadiness;
  live: boolean;
  running: boolean;
  approving: boolean;
  notifying: boolean;
  onRun: () => void;
  onApprove: () => void;
  onRetryEmail: () => void;
}): React.ReactElement {
  const blocked = account.red > 0 || account.amber > 0 || account.awaitingCosts || account.structuresTested < 1;
  const waitingForSetup = !live && account.onboardingVerificationStage !== "team_review";
  const automaticActionNeeded = account.neverRun || blocked || waitingForSetup
    || verificationBlocksApproval(account.verificationJob, account.ranAt);
  const warningsAccepted = assumptions.warningIds.length === 0 || (
    assumptions.reviewNote.trim().length > 0
    && assumptions.warningIds.every((id) => assumptions.acceptedWarningIds.includes(id))
    && assumptions.acceptedWarningIds.every((id) => assumptions.warningIds.includes(id))
  );
  return (
    <aside style={approvalPanel}>
      {live ? (
        <>
          <span style={{ ...statusChip("good"), alignSelf: "flex-start" }}>Live for client</span>
          <h3 style={{ ...sectionHeading, marginTop: 10 }}>This dashboard is unlocked</h3>
          <p style={panelBody}>The account passed its checks and team review. The client can use their real numbers now.</p>
          {account.onboardingApprovedAt && <p style={timestamp}>Approved {new Date(account.onboardingApprovedAt).toLocaleString()}</p>}
          <div style={notificationBox(account.onboardingNotification?.status)}>
            <b style={{ color: "var(--text)", fontSize: 12.5 }}>{notificationTitle(account.onboardingNotification)}</b>
            <p style={{ color: "var(--body)", fontSize: 12, lineHeight: 1.45, margin: "4px 0 0" }}>{notificationMessage(account.onboardingNotification)}</p>
          </div>
          {account.onboardingNotification?.status !== "sent" && (
            <button type="button" style={primaryButton} disabled={notifying} onClick={onRetryEmail}>{notifying ? "Sending…" : "Retry ready email"}</button>
          )}
        </>
      ) : (
        <>
          <h3 style={sectionHeading}>Approve this account</h3>
          <p style={panelBody}>
            {account.neverRun
              ? "Run the automatic checks before this account can be approved."
              : waitingForSetup
                ? "The preliminary checks passed, but the client must finish setup before you can approve the account."
                : blocked
                ? "Resolve every open item and rerun the checks before this dashboard can be unlocked."
                : assumptions.status === "not_loaded"
                  ? "The automatic checks are clean. Open the saved assumptions review before approving this account."
                  : assumptions.status === "loading"
                  ? "The automatic checks are clean. The saved assumptions are still loading, so approval stays locked."
                  : assumptions.status === "error"
                    ? "The automatic checks are clean, but the saved assumptions review did not load. Refresh before approving."
                    : assumptions.hardBlockers > 0
                      ? `Fix the ${assumptions.hardBlockers} open saved assumption${assumptions.hardBlockers === 1 ? "" : "s"} before this dashboard can be unlocked.`
                      : !warningsAccepted
                        ? "Review every yellow assumption, check the ones you accept for launch, and add the launch-review note before approving."
                : "The latest check is clean. Approving now gives the client access to their real dashboard."}
          </p>
          {account.onboardingVerificationStage === "team_review" && account.onboardingReviewRequestedAt && (
            <div style={notificationBox(account.teamReviewNotification?.status)}>
              <b style={{ color: "var(--text)", fontSize: 12.5 }}>{teamReviewNotificationTitle(account.teamReviewNotification)}</b>
              <p style={{ color: "var(--body)", fontSize: 12, lineHeight: 1.45, margin: "4px 0 0" }}>{teamReviewNotificationMessage(account.teamReviewNotification)}</p>
            </div>
          )}
          <ul style={impactList}>
            <li>The real dashboard unlocks immediately</li>
            <li>The client owner gets a ready email</li>
            <li>Later clean checks keep the dashboard live</li>
            <li>A genuine new problem returns here for review</li>
          </ul>
          {ready ? (
            <button type="button" style={approveButton} disabled={approving || running} onClick={onApprove}>{approving ? "Approving…" : "Approve and unlock dashboard"}</button>
          ) : automaticActionNeeded ? (
            <button type="button" style={primaryButton} disabled={running || waitingForSetup} onClick={onRun}>{running ? "Running checks…" : waitingForSetup ? "Waiting for client setup" : account.neverRun ? "Run checks now" : "Run checks again"}</button>
          ) : null}
          {!ready && <button type="button" style={disabledApprove} disabled>Approve and unlock dashboard</button>}
        </>
      )}
      {account.ranAt && <p style={timestamp}>Last checked {new Date(account.ranAt).toLocaleString()}</p>}
    </aside>
  );
}

function findingAction(finding: DataHealthFinding): { label: string; destination: string } {
  if (finding.rule === "data.buygoods_historical_source") return { label: "Review BuyGoods history", destination: "/connections" };
  if (finding.rule === "data.buygoods_live_source") return { label: "Review BuyGoods sales", destination: "/connections" };
  if (finding.rule === "data.overhead_history") return { label: "Review overhead history", destination: "/costs/overhead" };
  if (finding.rule === "data.acquisition_history") return { label: "Add older acquisition costs", destination: "/costs" };
  return { label: "Open product costs", destination: "/onboarding/chat?stage=costs" };
}

function notificationTitle(notification: ReadyNotification | null): string {
  if (notification?.status === "sent") return "Ready email sent";
  if (notification?.status === "no_recipient") return "Client owner email needed";
  if (notification?.status === "not_configured") return "Ready email is not connected";
  return "Ready email was not sent";
}

function notificationMessage(notification: ReadyNotification | null): string {
  if (notification?.status === "sent") return `Sent to ${notification.recipientCount} verified client owner${notification.recipientCount === 1 ? "" : "s"}.`;
  if (notification?.status === "no_recipient") return "No verified client owner email was found. Add the owner in Settings, then retry.";
  if (notification?.status === "not_configured") return "The email service is not configured yet. The dashboard is still live.";
  if (notification?.status === "failed") return "The dashboard is live, but the email provider failed. Retry when ready.";
  return "This account was approved before ready-email tracking was added. Send it now if the client still needs it.";
}

function teamReviewNotificationTitle(notification: TeamReviewNotification | null): string {
  if (notification?.status === "sent") return "Review email sent";
  if (notification?.status === "pending") return "Review alert saved";
  if (notification?.status === "not_configured") return "Review email is not connected";
  return "Review email was not sent";
}

function teamReviewNotificationMessage(notification: TeamReviewNotification | null): string {
  if (notification?.status === "sent") return "The team review is saved here, and the operator email was sent.";
  if (notification?.status === "pending") return "The team review is saved here. The operator email is still being sent.";
  if (notification?.status === "not_configured") return "The team review is saved here. Set up the operator email to get a second alert next time.";
  if (notification?.status === "failed") return "The team review is saved here, but the email provider failed.";
  return "The team review is saved here. No email result was recorded for this submission.";
}

function apiError(error: unknown, fallback: string): string {
  const detail = (error as { detail?: { error?: unknown } })?.detail?.error;
  return typeof detail === "string" && detail.trim() ? detail : fallback;
}

function summaryLine(account: AccountDataHealth): string {
  if (verificationIsRunning(account.verificationJob)) return "Checks are in progress. Open this account to see the current step.";
  if (account.neverRun) return "Never checked";
  const pieces = [
    ...(account.onboardingVerificationStage === "team_review" && account.onboardingReviewRequestedAt ? ["Submitted for team review"] : []),
    `${account.checksPassed.toLocaleString()} checks passed`,
  ];
  if (account.red > 0) pieces.push(`${account.red} math problem${account.red === 1 ? "" : "s"}`);
  if (account.amber > 0) pieces.push(`${account.amber} item${account.amber === 1 ? "" : "s"} need review`);
  if (account.ordersTested > 0) pieces.push(`${account.ordersTested.toLocaleString()} orders tested`);
  return pieces.join(" · ");
}

type Tone = "red" | "amber" | "good" | "neutral";
function accountStatus(account: AccountDataHealth): { label: string; tone: Tone } {
  if (verificationIsRunning(account.verificationJob)) return { label: "Checking", tone: "neutral" };
  if (verificationBlocksApproval(account.verificationJob, account.ranAt)) return { label: "Check stopped", tone: "amber" };
  if (account.neverRun) return { label: "Not checked", tone: "neutral" };
  if (account.red > 0) return { label: "Math problem", tone: "red" };
  if (account.amber > 0) return { label: "Needs review", tone: "amber" };
  if (account.onboardingVerificationStage === "verified") return { label: "Live for client", tone: "good" };
  if (account.onboardingVerificationStage !== "team_review") return { label: "Waiting for setup", tone: "neutral" };
  if (account.awaitingCosts || account.structuresTested < 1) return { label: "Needs review", tone: "amber" };
  return { label: "Checks clean", tone: "amber" };
}

function toneColor(tone: Tone): string {
  if (tone === "red") return "var(--bad)";
  if (tone === "amber") return "var(--warn, #E5A83B)";
  if (tone === "good") return "var(--good)";
  return "var(--body-faint)";
}

function SummaryPill({ label, value, tone }: { label: string; value: number; tone: Tone }): React.ReactElement {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 10, border: "1px solid var(--border)", background: "var(--surface)" }}>
      <span style={{ ...statusDot, background: toneColor(tone) }} />
      <span className="dd-num" style={{ fontSize: 16, fontWeight: 700, color: "var(--text)" }}>{value.toLocaleString()}</span>
      <span style={{ fontSize: 12, color: "var(--body)" }}>{label}</span>
    </div>
  );
}

const kicker: React.CSSProperties = { color: "var(--accent)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" };
const rowButton: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "14px 16px", background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit" };
const statusDot: React.CSSProperties = { width: 9, height: 9, borderRadius: "50%", flexShrink: 0 };
const statusChip = (tone: Tone): React.CSSProperties => ({
  fontSize: 11,
  fontWeight: 700,
  padding: "3px 9px",
  borderRadius: 999,
  whiteSpace: "nowrap",
  flexShrink: 0,
  background: tone === "red" ? "color-mix(in srgb, var(--bad) 14%, transparent)" : tone === "amber" ? "color-mix(in srgb, var(--warn, #E5A83B) 16%, transparent)" : tone === "good" ? "color-mix(in srgb, var(--good) 14%, transparent)" : "var(--surface-2)",
  color: tone === "red" ? "var(--bad)" : tone === "amber" ? "var(--warn, #E5A83B)" : tone === "good" ? "var(--good)" : "var(--body)",
});
const timeline: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12, background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: 14, marginBottom: 12 };
const timelineIcon: React.CSSProperties = { width: 24, height: 24, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", border: "1px solid var(--border)", color: "var(--body-faint)", fontWeight: 800, fontSize: 12 };
const timelineDone: React.CSSProperties = { background: "var(--accent)", borderColor: "var(--accent)", color: "var(--accent-contrast)" };
const timelineActive: React.CSSProperties = { background: "color-mix(in srgb, var(--warn, #E5A83B) 14%, var(--surface))", borderColor: "var(--warn, #E5A83B)", color: "var(--warn, #E5A83B)" };
const reviewGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 310px), 1fr))", gap: 12, alignItems: "start" };
const sectionCard: React.CSSProperties = { background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: 14 };
const sectionHeadingRow: React.CSSProperties = { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 };
const sectionHeading: React.CSSProperties = { color: "var(--text)", fontSize: 15, fontWeight: 750, margin: 0 };
const findingRow: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderLeft: "3px solid", borderRadius: "var(--r-sm)", padding: "11px 12px" };
const noteRow: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: "10px 12px" };
const smallButton: React.CSSProperties = { marginTop: 9, background: "var(--surface-2)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: "7px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };
const passedList: React.CSSProperties = { color: "var(--body)", fontSize: 12.5, lineHeight: 1.55, margin: "10px 0 0", paddingLeft: 20, display: "flex", flexDirection: "column", gap: 6 };
const approvalPanel: React.CSSProperties = { display: "flex", flexDirection: "column", alignItems: "stretch", position: "sticky", top: 16, background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: 14 };
const panelBody: React.CSSProperties = { color: "var(--body)", fontSize: 12.5, lineHeight: 1.55, margin: "8px 0 0" };
const impactList: React.CSSProperties = { color: "var(--body)", fontSize: 12.5, lineHeight: 1.5, paddingLeft: 20, margin: "12px 0", display: "flex", flexDirection: "column", gap: 7 };
const primaryButton: React.CSSProperties = { width: "100%", marginTop: 10, background: "var(--accent)", color: "var(--accent-contrast)", border: "none", borderRadius: "var(--r-sm)", padding: "10px 12px", fontSize: 12.5, fontWeight: 750, cursor: "pointer", fontFamily: "inherit" };
const approveButton: React.CSSProperties = { ...primaryButton, background: "var(--good)", color: "white" };
const disabledApprove: React.CSSProperties = { ...primaryButton, background: "var(--surface-3)", color: "var(--body-faint)", cursor: "not-allowed" };
const timestamp: React.CSSProperties = { color: "var(--body-faint)", fontSize: 11, margin: "10px 0 0" };
const notificationBox = (status?: NotificationStatus | TeamReviewNotificationStatus): React.CSSProperties => ({ background: "var(--surface)", border: `1px solid ${status === "sent" ? "color-mix(in srgb, var(--good) 45%, var(--border))" : "var(--border)"}`, borderRadius: "var(--r-sm)", padding: "10px 11px", marginTop: 12 });
